import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { quizAPI, categoryAPI, courseAPI } from "@/lib/api";
import { CourseGrouping } from "@/components/CourseGrouping";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  ClipboardList, 
  Pencil, 
  Trash2,
  ChevronRight,
  ChevronDown,
  Loader2,
  Eye,
  EyeOff,
  Clock,
  Target,
  CheckCircle,
  XCircle,
  HelpCircle,
  Users,
  Trophy,
  BarChart3,
  FileText,
  X
} from "lucide-react";
import { toast } from "sonner";
import { formatDate, cn } from "@/lib/utils";
import { CategoryManagerButton } from "@/components/CategoryManagerButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";

export default function QuizPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [userAttempts, setUserAttempts] = useState({});
  const [expandedQuiz, setExpandedQuiz] = useState(null);
  const [quizAttempts, setQuizAttempts] = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [courses, setCourses] = useState([]);
  // Attempt detail & delete states
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteAttemptId, setDeleteAttemptId] = useState(null);
  const [deletingAttempt, setDeletingAttempt] = useState(false);

  const canManage = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    loadQuizzes();
    categoryAPI.list(workspaceId).then(res => setCategories(res.data || [])).catch(() => {});
    courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
  }, [workspaceId]);

  const loadQuizzes = async () => {
    try {
      const response = await quizAPI.list(workspaceId);
      setQuizzes(response.data);
      
      // Load attempts for each quiz for the current user
      if (!canManage) {
        const attemptsMap = {};
        for (const quiz of response.data) {
          try {
            const attemptsRes = await quizAPI.getMyAttempts(workspaceId, quiz.id);
            attemptsMap[quiz.id] = attemptsRes.data;
          } catch (e) {
            attemptsMap[quiz.id] = [];
          }
        }
        setUserAttempts(attemptsMap);
      }
    } catch (error) {
      console.error("Failed to load quizzes:", error);
      toast.error("Erreur lors du chargement des quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);

    try {
      await quizAPI.delete(workspaceId, deleteId);
      setQuizzes(quizzes.filter(q => q.id !== deleteId));
      toast.success("Quiz supprimé");
    } catch (error) {
      console.error("Failed to delete quiz:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleToggleVisibility = async (quizId) => {
    try {
      const response = await quizAPI.toggleVisibility(workspaceId, quizId);
      setQuizzes(quizzes.map(q => 
        q.id === quizId ? { ...q, is_visible: response.data.is_visible } : q
      ));
      toast.success(response.data.message);
    } catch (error) {
      console.error("Failed to toggle visibility:", error);
      toast.error("Erreur lors de la modification de la visibilité");
    }
  };

  const getQuestionTypeIcon = (type) => {
    switch (type) {
      case "qcm": return "QCM";
      case "multiple": return "Multi";
      case "true_false": return "V/F";
      case "short_answer": return "Texte";
      default: return type;
    }
  };

  const getUserQuizStatus = (quizId) => {
    const attempts = userAttempts[quizId] || [];
    if (attempts.length === 0) return null;
    
    const bestAttempt = attempts.reduce((best, current) => 
      current.percentage > (best?.percentage || 0) ? current : best
    , null);
    
    return bestAttempt;
  };

  const toggleQuizExpand = async (quizId) => {
    if (expandedQuiz === quizId) {
      setExpandedQuiz(null);
      return;
    }
    setExpandedQuiz(quizId);
    setLoadingAttempts(true);
    try {
      const res = await quizAPI.getAttempts(workspaceId, quizId);
      setQuizAttempts(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des tentatives");
      setQuizAttempts([]);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const handleViewAttempt = async (quizId, attemptId) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const res = await quizAPI.getAttemptDetail(workspaceId, quizId, attemptId);
      setAttemptDetail(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement de la tentative");
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDeleteAttempt = async () => {
    if (!deleteAttemptId) return;
    setDeletingAttempt(true);
    try {
      await quizAPI.deleteAttempt(workspaceId, expandedQuiz, deleteAttemptId);
      setQuizAttempts(quizAttempts.filter(a => a.id !== deleteAttemptId));
      toast.success("Tentative supprimée");
    } catch (e) {
      toast.error("Erreur lors de la suppression de la tentative");
    } finally {
      setDeletingAttempt(false);
      setDeleteAttemptId(null);
    }
  };

  // Category-based grouping
  const hasCategories = categories.length > 0;

  const quizzesByGroup = quizzes.reduce((acc, quiz) => {
    let groupKey;
    if (hasCategories) {
      groupKey = quiz.category_id || "__uncategorized__";
    } else {
      groupKey = quiz.chapter || "Sans chapitre";
    }
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(quiz);
    return acc;
  }, {});

  const sortedGroups = (() => {
    if (hasCategories) {
      const result = [];
      categories.forEach(cat => {
        if (quizzesByGroup[cat.id]) {
          result.push({ key: cat.id, label: cat.name, color: cat.color, items: quizzesByGroup[cat.id] });
        }
      });
      if (quizzesByGroup["__uncategorized__"]) {
        result.push({ key: "__uncategorized__", label: "Sans catégorie", color: null, items: quizzesByGroup["__uncategorized__"] });
      }
      return result;
    } else {
      return Object.entries(quizzesByGroup)
        .sort((a, b) => {
          if (a[0] === "Sans chapitre") return 1;
          if (b[0] === "Sans chapitre") return -1;
          return 0;
        })
        .map(([key, items]) => ({ key, label: key, color: null, items }));
    }
  })();

  // Auto-select first tab
  useEffect(() => {
    if (quizzes.length === 0) return;
    let currentValid = activeTab && sortedGroups.some(g => g.key === activeTab);
    if (!currentValid) {
      if (sortedGroups.length > 0) {
        setActiveTab(sortedGroups[0].key);
      } else {
        setActiveTab(null);
      }
    }
  }, [quizzes, categories, activeTab, sortedGroups]);

  // Group items within active tab by chapter (for category mode)
  const getChapterGroups = (items) => {
    const grouped = items.reduce((acc, quiz) => {
      const chapter = quiz.chapter || "Sans chapitre";
      if (!acc[chapter]) acc[chapter] = [];
      acc[chapter].push(quiz);
      return acc;
    }, {});
    return Object.entries(grouped).sort((a, b) => {
      if (a[0] === "Sans chapitre") return 1;
      if (b[0] === "Sans chapitre") return -1;
      return 0;
    });
  };

  const renderQuizCard = (quiz, index, courseColor) => {
    const userStatus = getUserQuizStatus(quiz.id);
    return (
      <Card 
        key={quiz.id} 
        className={cn(
          "card-hover transition-all relative overflow-hidden pl-2",
          quiz.is_visible === false && "opacity-60 border-dashed"
        )}
        style={courseColor ? { borderColor: `${courseColor}33` } : {}}
        data-testid={`quiz-card-${index}`}
      >
        {courseColor && (
          <div 
            className="absolute left-0 top-0 bottom-0 w-1.5 transition-all" 
            style={{ backgroundColor: courseColor }}
          />
        )}
        <CardContent className="flex items-center gap-4 p-4">
          <div 
            className="flex h-12 w-12 items-center justify-center rounded-lg shrink-0 transition-colors"
            style={courseColor ? { backgroundColor: `${courseColor}15` } : {}}
          >
            <ClipboardList 
              className={cn("h-6 w-6 text-purple-600", courseColor && "text-foreground")}
              style={courseColor ? { color: courseColor } : {}}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Link 
                to={`/workspace/${workspaceId}/quiz/${quiz.id}/take`}
                className="font-semibold hover:text-primary transition-colors"
              >
                {quiz.title}
              </Link>
              {canManage && quiz.is_visible === false && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
                  Caché
                </Badge>
              )}
              {userStatus && (
                <Badge 
                  variant={userStatus.passed ? "default" : "destructive"}
                  className={userStatus.passed ? "bg-green-500" : ""}
                >
                  {userStatus.passed ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Réussi</>
                  ) : (
                    <><XCircle className="h-3 w-3 mr-1" /> {userStatus.percentage}%</>
                  )}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
              {quiz.description || "Pas de description"}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <HelpCircle className="h-3 w-3" />
                {quiz.questions?.length || 0} questions
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {quiz.time_limit_minutes} min
              </span>
              <span className="flex items-center gap-1">
                <Target className="h-3 w-3" />
                {quiz.passing_score}% pour réussir
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canManage && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleVisibility(quiz.id)}
                      >
                        {quiz.is_visible === false ? (
                          <EyeOff className="h-4 w-4 text-yellow-600" />
                        ) : (
                          <Eye className="h-4 w-4 text-green-600" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {quiz.is_visible === false 
                        ? "Rendre visible aux candidats" 
                        : "Cacher aux candidats"
                      }
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/workspace/${workspaceId}/quiz/${quiz.id}/edit`)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteId(quiz.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </>
            )}
            {canManage ? (
              <Button variant="ghost" size="icon" onClick={() => toggleQuizExpand(quiz.id)}>
                {expandedQuiz === quiz.id 
                  ? <ChevronDown className="h-4 w-4" /> 
                  : <ChevronRight className="h-4 w-4" />
                }
              </Button>
            ) : (
              <Link to={userStatus ? '#' : `/workspace/${workspaceId}/quiz/${quiz.id}/take`}>
                <Button variant="ghost" size="icon" disabled={!!userStatus}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            )}
          </div>
        </CardContent>

        {/* Admin Attempts Dashboard */}
        {canManage && expandedQuiz === quiz.id && (
          <div className="border-t bg-slate-50/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="h-4 w-4 text-purple-600" />
              <h3 className="font-semibold text-sm">Résultats des tentatives</h3>
            </div>
            {loadingAttempts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
              </div>
            ) : quizAttempts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Aucune tentative pour ce quiz</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border bg-white">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Candidat</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                      <TableHead className="text-center">Pourcentage</TableHead>
                      <TableHead className="text-center">Résultat</TableHead>
                      <TableHead className="text-center">Date</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quizAttempts.map((attempt) => (
                      <TableRow key={attempt.id}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={attempt.user_picture} />
                              <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                                {attempt.user_name?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-sm">{attempt.user_name || 'Inconnu'}</p>
                              <p className="text-[11px] text-muted-foreground">{attempt.user_email || ''}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-semibold">{attempt.score}/{attempt.total_points}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Progress value={attempt.percentage} className="h-2 w-16" />
                            <span className="text-sm font-medium">{attempt.percentage}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          {attempt.passed ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                              <CheckCircle className="h-3 w-3 mr-1" /> Réussi
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100">
                              <XCircle className="h-3 w-3 mr-1" /> Échoué
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-sm text-muted-foreground">
                          {attempt.completed_at 
                            ? new Date(attempt.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : '-'
                          }
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => handleViewAttempt(quiz.id, attempt.id)}
                                  >
                                    <Eye className="h-3.5 w-3.5 text-blue-600" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Voir le détail</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    onClick={() => setDeleteAttemptId(attempt.id)}
                                  >
                                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Supprimer la tentative</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Quiz</h1>
            <p className="text-muted-foreground mt-1">
              {quizzes.length} quiz dans cet espace
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <CategoryManagerButton />
              <Button className="gap-2" onClick={() => navigate(`/workspace/${workspaceId}/quiz/new`)}>
                <Plus className="h-4 w-4" />
                Nouveau Quiz
              </Button>
            </div>
          )}
        </div>

        {/* Empty State */}
        {quizzes.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">Aucun quiz</h3>
              <p className="text-muted-foreground mt-2 text-center max-w-md">
                {canManage
                  ? "Commencez par créer votre premier quiz."
                  : "Aucun quiz n'est disponible pour le moment."}
              </p>
              {canManage && (
                <Button className="mt-4 gap-2" onClick={() => navigate(`/workspace/${workspaceId}/quiz/new`)}>
                  <Plus className="h-4 w-4" />
                  Créer un quiz
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div>
            {/* Category Tabs */}
            {sortedGroups.length > 1 || hasCategories ? (
              <>
                <div className="flex flex-wrap gap-3 mb-6 relative z-10 w-full">
                  {sortedGroups.map((group) => (
                    <div 
                      key={group.key}
                      onClick={() => setActiveTab(group.key)}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all select-none min-w-max",
                        activeTab === group.key 
                          ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary/20" 
                          : "bg-card hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {group.color ? (
                        <div className="w-3.5 h-3.5 rounded" style={{ backgroundColor: group.color }} />
                      ) : (
                        <ClipboardList className={cn("h-4 w-4", activeTab === group.key ? "text-primary" : "")} />
                      )}
                      <span className={cn("font-medium text-sm", activeTab === group.key ? "text-foreground" : "")}>
                        {group.label}
                      </span>
                      <span className="text-xs opacity-70 ml-1">{group.items.length}</span>
                    </div>
                  ))}
                </div>

                {/* Active Tab Content */}
                {sortedGroups.map((group) => {
                  if (group.key !== activeTab) return null;

                  // Show items grouped by course
                  return (
                    <div key={group.key} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                      <CourseGrouping
                        items={group.items}
                        courses={courses}
                        renderItem={renderQuizCard}
                        emptyLabel="Sans cours"
                        icon={ClipboardList}
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              /* Fallback: group by course directly */
              <CourseGrouping
                items={quizzes}
                courses={courses}
                renderItem={renderQuizCard}
                emptyLabel="Sans cours"
                icon={ClipboardList}
              />
            )}
          </div>
        )}

        {/* Delete Quiz Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce quiz ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le quiz et toutes les tentatives seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Attempt Confirmation Dialog */}
        <AlertDialog open={!!deleteAttemptId} onOpenChange={() => setDeleteAttemptId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette tentative ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. La tentative sera définitivement supprimée et le candidat pourra repasser le quiz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteAttempt} disabled={deletingAttempt}>
                {deletingAttempt && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Attempt Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                Détail de la tentative
              </DialogTitle>
            </DialogHeader>
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : attemptDetail ? (
              <div className="space-y-6">
                {/* User info & summary */}
                <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50 border">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={attemptDetail.user_picture} />
                    <AvatarFallback className="bg-purple-100 text-purple-700">
                      {attemptDetail.user_name?.charAt(0) || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold">{attemptDetail.user_name || 'Inconnu'}</p>
                    <p className="text-sm text-muted-foreground">{attemptDetail.user_email || ''}</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${attemptDetail.passed ? 'text-green-600' : 'text-red-600'}`}>
                      {attemptDetail.percentage}%
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {attemptDetail.score}/{attemptDetail.total_points} pts
                    </p>
                  </div>
                </div>

                {/* Status badge & date */}
                <div className="flex items-center justify-between">
                  <Badge className={attemptDetail.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {attemptDetail.passed ? <><CheckCircle className="h-3 w-3 mr-1" /> Réussi</> : <><XCircle className="h-3 w-3 mr-1" /> Échoué</>}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {attemptDetail.completed_at 
                      ? new Date(attemptDetail.completed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '-'
                    }
                  </span>
                </div>

                {/* Answers detail */}
                {attemptDetail.results && attemptDetail.results.length > 0 ? (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Réponses ({attemptDetail.results.filter(r => r.is_correct).length}/{attemptDetail.results.length} correctes)
                    </h3>
                    {attemptDetail.results.map((item, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          item.is_correct 
                            ? 'border-green-200 bg-green-50/50' 
                            : 'border-red-200 bg-red-50/50'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {item.is_correct ? (
                            <CheckCircle className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{item.question_text || `Question ${idx + 1}`}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              <span className="font-medium">Réponse :</span>{' '}
                              {item.user_answer 
                                ? (Array.isArray(item.user_answer) ? item.user_answer.join(', ') : String(item.user_answer))
                                : <span className="italic">Pas de réponse</span>
                              }
                            </p>
                            {!item.is_correct && item.correct_answers && (
                              <p className="text-xs text-green-700 mt-1">
                                <span className="font-medium">Correct :</span>{' '}
                                {Array.isArray(item.correct_answers) ? item.correct_answers.join(', ') : item.correct_answers}
                              </p>
                            )}
                            {item.explanation && (
                              <p className="text-xs text-muted-foreground mt-1 italic">{item.explanation}</p>
                            )}
                          </div>
                          <Badge variant="outline" className="shrink-0 text-[10px]">
                            {item.points_earned}/{item.points_possible} pt{item.points_possible > 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : attemptDetail.answers ? (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <ClipboardList className="h-4 w-4" />
                      Réponses brutes
                    </h3>
                    <div className="p-3 rounded-lg border bg-slate-50">
                      <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(attemptDetail.answers, null, 2)}
                      </pre>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">Aucun détail disponible pour cette tentative</p>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </WorkspaceLayout>
  );
}
